import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { CircleAlert, Plus, Trash2, User } from 'lucide-react';
import { customersApi, usersApi } from '@/lib/api';
import {
  getEmailErrorMessage,
  getPhoneErrorMessage,
  normalizePhoneNumber,
  filterPhoneInput,
  formatApiError,
} from '@/lib/validation';
import { Avatar, Button, Callout, Card, Combobox, EmptyState, IconButton, Skeleton, statusInfo } from '@elbklar/ui';
import { CrmPageHeader } from '@/components/layout/CrmPageHeader';
import { TextareaField, TextField } from '@/components/form/Felder';
import { LostReasonModal } from '@/features/customers/components/LostReasonModal';
import { FollowUpModal, fromDatetimeLocal, toDatetimeLocal } from '@/features/customers/components/FollowUpModal';
import {
  KUNDE_STATUSES,
  LEAD_STATUSES,
  customerBusinessTypeOptions,
  customerTypeLabels,
  type CustomerCreate,
  type CustomerStatus,
  type CustomerType,
} from '@/types';

// Gleiche Werte wie im ConvertLeadModal: was dort beim Umwandeln gesetzt wird,
// muss hier auch wieder aenderbar sein. Eine Liste fuer alle, aus @/types.
const businessTypeOptions = customerBusinessTypeOptions;

const typeOptions = (Object.keys(customerTypeLabels) as CustomerType[]).map((value) => ({
  value,
  label: customerTypeLabels[value],
}));

const isLostStatus = (s: CustomerStatus) => s === 'lost' || s === 'lead_lost';

const industryOptions = [
  { value: 'privat_immobilien', label: 'Privat Immobilien' },
  { value: 'wohnungswirtschaft', label: 'Wohnungswirtschaft' },
  { value: 'gewerbe', label: 'Gewerbe' },
  { value: 'industrie', label: 'Industrie' },
  { value: 'oeffentlich', label: 'Öffentlicher Sektor' },
];

const acquisitionOptions = [
  { value: 'website', label: 'Website' },
  { value: 'empfehlung', label: 'Empfehlung' },
  { value: 'kaltakquise', label: 'Kaltakquise' },
  { value: 'partner', label: 'Partner' },
  { value: 'messe', label: 'Messe' },
  { value: 'social_media', label: 'Social Media' },
  { value: 'sonstige', label: 'Sonstige' },
];

interface InlineContact {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  mobile: string;
  role: string;
  is_primary: boolean;
}

interface ContactErrors {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  general?: string;
}

interface FormData extends CustomerCreate {}

export function CustomerFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = !!id;

  // State for inline contacts (only for new customers)
  const [contacts, setContacts] = useState<InlineContact[]>([]);
  const [showContactForm, setShowContactForm] = useState(false);
  const [newContact, setNewContact] = useState<InlineContact>({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    mobile: '',
    role: '',
    is_primary: false,
  });
  const [contactErrors, setContactErrors] = useState<ContactErrors>({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const { data: customer, isLoading: isLoadingCustomer } = useQuery({
    queryKey: ['customers', id],
    queryFn: () => customersApi.get(id!),
    enabled: isEdit,
  });

  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.list(),
  });

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isDirty },
  } = useForm<FormData>({
    defaultValues: {
      company_name: '',
      type: 'company',
      status: 'viewing_scheduled',
      industry: undefined,
      customer_business_type: undefined,
      acquisition_source: undefined,
      assigned_to: undefined,
      website: '',
      vat_number: '',
      street: '',
      postal_code: '',
      city: '',
      managed_objects_count: undefined,
      notes: '',
      tags: [],
      lost_reason: null,
      follow_up_at: null,
    },
    // Das Formular traegt Status, Verlustgrund und Wiedervorlage mit: Das
    // Backend verlangt zum Status „Verloren" einen Grund und zu „Interessiert
    // aber spaeter" ein Datum. Bis zum 22.10.2026 schickte die Seite nur den
    // Status — ein Kunde in einem dieser Status liess sich ueber „Bearbeiten"
    // nie mehr speichern (Audit CRM-2). ``type`` kommt aus dem Datensatz, nicht
    // fest „company": sonst wurde eine Person beim Speichern still zur Firma.
    values: customer ? {
      company_name: customer.company_name,
      type: customer.type,
      status: customer.status,
      industry: customer.industry || undefined,
      // Ein Altwert, den core nicht kennt, laesst sich nicht mehr zurueckschreiben;
      // das Feld bleibt dann leer und der Nutzer waehlt neu (Audit C9).
      customer_business_type: businessTypeOptions.some((o) => o.value === customer.customer_business_type)
        ? customer.customer_business_type || undefined
        : undefined,
      acquisition_source: customer.acquisition_source || undefined,
      assigned_to: customer.assigned_to || undefined,
      website: customer.website || '',
      vat_number: customer.vat_number || '',
      street: customer.street || '',
      postal_code: customer.postal_code || '',
      city: customer.city || '',
      managed_objects_count: customer.managed_objects_count || undefined,
      notes: customer.notes || '',
      tags: customer.tags || [],
      lost_reason: customer.lost_reason ?? null,
      follow_up_at: customer.follow_up_at ?? null,
    } : undefined,
  });

  // Statuswechsel mit Pflichtangabe: Dieselben Dialoge wie auf der Detailseite
  // und in der Liste. Der Status wird erst gesetzt, wenn der Dialog bestaetigt ist.
  const status = watch('status');
  const [pendingStatus, setPendingStatus] = useState<CustomerStatus | null>(null);
  const [showLost, setShowLost] = useState(false);
  const [showFollowUp, setShowFollowUp] = useState(false);
  // Ein Lead ist derselbe Datensatz wie ein Kunde, nur mit einem Status aus
  // LEAD_STATUSES — und `/customers/<lead-id>/edit` laedt ihn deshalb auch.
  // Diese Seite bleibt der einzige Ort, an dem sich die Stammdaten eines Leads
  // bearbeiten lassen; was sie seit dem 29.10.2026 nicht mehr tut, ist ihm
  // alle sieben Kundenstatus anzubieten. Wer das benutzte, machte aus einem
  // Lead einen Kunden OHNE Kundennummer, OHNE Aktivierungsdatum und OHNE
  // Anfrage — anders als „Als Kunde anlegen" am Lead, das alle drei zieht.
  // Der Weg vom Lead zum Kunden laeuft seitdem nur ueber jenen Knopf, der
  // Rueckweg ueber „Wieder als Lead fuehren" am Kunden.
  const istLead = !!customer && LEAD_STATUSES.includes(customer.status);
  const erlaubteStatus = istLead ? LEAD_STATUSES : KUNDE_STATUSES;
  const statusOptions = (
    erlaubteStatus.includes(status) ? erlaubteStatus : [status, ...erlaubteStatus]
  ).map((s) => ({ value: s, label: statusInfo(s).label }));

  const statusWaehlen = (value: string) => {
    const next = value as CustomerStatus;
    if (!next || next === status) return;
    if (isLostStatus(next)) {
      setPendingStatus(next);
      setShowLost(true);
      return;
    }
    if (next === 'interested_later') {
      setPendingStatus(next);
      setShowFollowUp(true);
      return;
    }
    setValue('status', next, { shouldDirty: true });
  };
  const dialogeSchliessen = () => {
    setPendingStatus(null);
    setShowLost(false);
    setShowFollowUp(false);
  };

  // Track unsaved changes
  useEffect(() => {
    setHasUnsavedChanges(isDirty || contacts.length > 0 || showContactForm);
  }, [isDirty, contacts.length, showContactForm]);

  // Warn before leaving with unsaved changes (browser back/refresh)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const createMutation = useMutation({
    mutationFn: customersApi.create,
    onSuccess: (data) => {
      setHasUnsavedChanges(false);
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      navigate(`/customers/${data.id}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<CustomerCreate>) => customersApi.update(id!, data),
    onSuccess: () => {
      setHasUnsavedChanges(false);
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      navigate(`/customers/${id}`);
    },
  });

  // Ein geleertes Feld muss als null rausgehen, nicht als "" und nicht als
  // undefined: undefined liesse der PATCH unangetastet (exclude_unset), das
  // Feld waere also nicht mehr loeschbar. Die Objektzahl kommt aus dem
  // number-Input als String und wird hier zur Zahl.
  const normalisiere = (data: FormData): FormData => {
    const leerZuNull = (wert: string | null | undefined): string | null =>
      wert && wert.trim() !== '' ? wert : null;

    const objekte = data.managed_objects_count;
    const objekteZahl =
      objekte === undefined || objekte === null || `${objekte}`.trim() === ''
        ? null
        : Number(objekte);

    return {
      ...data,
      website: leerZuNull(data.website),
      vat_number: leerZuNull(data.vat_number),
      street: leerZuNull(data.street),
      postal_code: leerZuNull(data.postal_code),
      city: leerZuNull(data.city),
      managed_objects_count: Number.isFinite(objekteZahl) ? objekteZahl : null,
      // Nur zum passenden Status mitschicken; sonst bliebe ein alter Grund am
      // Kunden kleben, nachdem er laengst wieder „Angebot gesendet" ist.
      lost_reason: isLostStatus(data.status) ? leerZuNull(data.lost_reason) : null,
      follow_up_at: data.status === 'interested_later' ? data.follow_up_at || null : null,
    };
  };

  const onSubmit = (data: FormData) => {
    if (isEdit) {
      updateMutation.mutate(normalisiere(data));
    } else {
      // Include contacts when creating
      createMutation.mutate({
        ...normalisiere(data),
        contacts: contacts.length > 0 ? contacts : undefined,
      });
    }
  };

  const validateContact = (contact: InlineContact): ContactErrors => {
    const errors: ContactErrors = {};

    // Name validieren
    if (!contact.first_name.trim()) {
      errors.first_name = 'Vorname fehlt';
    }
    if (!contact.last_name.trim()) {
      errors.last_name = 'Nachname fehlt';
    }

    // E-Mail validieren
    const emailErr = getEmailErrorMessage(contact.email);
    if (emailErr) {
      errors.email = emailErr;
    }

    // Telefon validieren
    const phoneErr = getPhoneErrorMessage(contact.phone);
    if (phoneErr) {
      errors.phone = phoneErr;
    }

    // Mobil validieren
    const mobileErr = getPhoneErrorMessage(contact.mobile);
    if (mobileErr) {
      errors.mobile = mobileErr;
    }

    return errors;
  };

  const addContact = () => {
    const errors = validateContact(newContact);
    setContactErrors(errors);

    if (Object.keys(errors).length === 0) {
      // Normalize phone numbers before adding
      const contactToAdd = {
        ...newContact,
        phone: normalizePhoneNumber(newContact.phone),
        mobile: normalizePhoneNumber(newContact.mobile),
        is_primary: contacts.length === 0,
      };
      setContacts([...contacts, contactToAdd]);
      setNewContact({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        mobile: '',
        role: '',
        is_primary: false,
      });
      setContactErrors({});
      setShowContactForm(false);
    }
  };

  const removeContact = (index: number) => {
    const updated = contacts.filter((_, i) => i !== index);
    // If we removed the primary, make the first one primary
    if (contacts[index].is_primary && updated.length > 0) {
      updated[0].is_primary = true;
    }
    setContacts(updated);
  };

  const zurueckZiel = !isEdit ? '/customers' : istLead ? `/leads/${id}` : `/customers/${id}`;

  const isSubmitting = createMutation.isPending || updateMutation.isPending;
  const mutationError = createMutation.error || updateMutation.error;

  if (isEdit && isLoadingCustomer) {
    return (
      <div className="space-y-6 pt-4">
        <div className="flex items-center gap-4">
          <Skeleton className="size-10 rounded-lg" />
          <Skeleton className="h-8 w-48" />
        </div>
        <Skeleton className="h-64 w-full rounded-lg" />
        <Skeleton className="h-48 w-full rounded-lg" />
      </div>
    );
  }

  return (
    <div>
      {/* Beim Lead heisst die Seite, was sie ist, und der Weg zurueck fuehrt
          auf die Leadseite — sonst laedt /customers/<lead-id> und leitet
          sofort weiter. */}
      <CrmPageHeader
        size="page"
        zurueck={{ to: zurueckZiel, label: 'Zurück' }}
        title={isEdit ? (istLead ? 'Lead bearbeiten' : 'Kunde bearbeiten') : 'Kunde anlegen'}
        subtitle={isEdit ? customer?.company_name : undefined}
      />

      {/* API Error */}
      {mutationError && (
        <Callout tone="danger" icon={CircleAlert} title="Speichern fehlgeschlagen" boxed className="mb-6">
          {formatApiError(mutationError)}
        </Callout>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="mt-4 space-y-6">
        {/* General Information */}
        <Card title="Kundendaten" description="Grundlegende Informationen">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Controller
                name="company_name"
                control={control}
                rules={{ required: 'Firmenname fehlt' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Firmenname"
                    required
                    error={errors.company_name?.message}
                    placeholder="Muster GmbH"
                  />
                )}
              />
              <Controller
                name="type"
                control={control}
                render={({ field }) => (
                  <Combobox
                    value={field.value}
                    onChange={(v) => field.onChange(v as CustomerType)}
                    label="Art"
                    options={typeOptions}
                    placeholder="Firma oder Privatperson"
                  />
                )}
              />
              <Combobox
                value={status}
                onChange={statusWaehlen}
                label="Status"
                options={statusOptions}
                placeholder="Status wählen"
              />
              {isLostStatus(status) && (
                <Controller
                  name="lost_reason"
                  control={control}
                  rules={{ required: 'Grund fehlt; ein verlorener Kunde braucht einen' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value || ''}
                      label="Grund für Verloren"
                      required
                      error={errors.lost_reason?.message}
                      placeholder="Preis zu hoch"
                    />
                  )}
                />
              )}
              {status === 'interested_later' && (
                <Controller
                  name="follow_up_at"
                  control={control}
                  rules={{ required: 'Wiedervorlage fehlt; „Später“ braucht ein Datum' }}
                  render={({ field }) => (
                    <TextField
                      type="datetime-local"
                      label="Wiedervorlage am"
                      required
                      value={toDatetimeLocal(field.value)}
                      onChange={(e) => field.onChange(fromDatetimeLocal(e.target.value))}
                      onBlur={field.onBlur}
                      error={errors.follow_up_at?.message}
                    />
                  )}
                />
              )}
              <Controller
                name="industry"
                control={control}
                render={({ field }) => (
                  <Combobox
                    value={field.value || ''}
                    onChange={field.onChange}
                    label="Branche"
                    options={industryOptions}
                    placeholder="Branche wählen"
                  />
                )}
              />
              <Controller
                name="customer_business_type"
                control={control}
                render={({ field }) => (
                  <Combobox
                    value={field.value || ''}
                    onChange={field.onChange}
                    label="Geschäftstyp"
                    options={businessTypeOptions}
                    placeholder="Geschäftstyp wählen"
                  />
                )}
              />
              <Controller
                name="acquisition_source"
                control={control}
                render={({ field }) => (
                  <Combobox
                    value={field.value || ''}
                    onChange={field.onChange}
                    label="Akquisequelle"
                    options={acquisitionOptions}
                    placeholder="Woher der Kunde kam"
                  />
                )}
              />
              <Controller
                name="assigned_to"
                control={control}
                render={({ field }) => (
                  <Combobox
                    value={field.value || ''}
                    onChange={field.onChange}
                    label="Zuständiger Mitarbeiter"
                    options={users.map(u => ({ value: u.id, label: u.name }))}
                    placeholder="Mitarbeiter wählen"
                  />
                )}
              />
            </div>
        </Card>

        {/* Anschrift und weitere Stammdaten.
            Diese Felder werden beim Umwandeln eines Leads erfasst und standen
            danach in keinem Formular mehr — sichtbar auf der Detailseite, aber
            nicht mehr korrigierbar. */}
        <Card title="Anschrift und Stammdaten" description="Sitz des Kunden und formale Angaben">
            <div className="grid grid-cols-1 md:grid-cols-6 gap-5">
              <div className="md:col-span-6">
                <Controller
                  name="street"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value || ''}
                      label="Straße und Hausnummer"
                      placeholder="Musterstraße 1"
                    />
                  )}
                />
              </div>
              <div className="md:col-span-2">
                <Controller
                  name="postal_code"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} value={field.value || ''} label="PLZ" placeholder="01099" />
                  )}
                />
              </div>
              <div className="md:col-span-4">
                <Controller
                  name="city"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} value={field.value || ''} label="Ort" placeholder="Dresden" />
                  )}
                />
              </div>
              <div className="md:col-span-3">
                <Controller
                  name="website"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} value={field.value || ''} label="Website" placeholder="www.beispiel.de" />
                  )}
                />
              </div>
              <div className="md:col-span-3">
                <Controller
                  name="vat_number"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} value={field.value || ''} label="USt-IdNr." placeholder="DE123456789" />
                  )}
                />
              </div>
              <div className="md:col-span-3">
                <Controller
                  name="managed_objects_count"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value ?? ''}
                      label="Verwaltete Objekte"
                      type="number"
                      min={0}
                    />
                  )}
                />
              </div>
            </div>
        </Card>

        {/* Contacts - only show for new customers */}
        {!isEdit && (
          <Card
            title="Ansprechpartner"
            description="Kontaktpersonen beim Kunden"
            actions={
              !showContactForm && (
                <Button type="button" size="sm" icon={Plus} onClick={() => setShowContactForm(true)}>
                  Hinzufügen
                </Button>
              )
            }
          >
              {/* Existing contacts */}
              {contacts.length > 0 && (
                <div className="space-y-3 mb-4">
                  {contacts.map((contact, index) => (
                    <div key={index} className="flex items-center justify-between rounded-lg bg-muted p-3">
                      <div className="flex items-center gap-3">
                        <Avatar name={`${contact.first_name} ${contact.last_name}`} size="lg" />
                        <div>
                          <div className="font-medium">
                            {contact.first_name} {contact.last_name}
                            {contact.is_primary && <span className="ml-2 t-label text-highlight">Primär</span>}
                          </div>
                          <div className="t-small text-muted-foreground">
                            {[contact.role, contact.email, contact.phone, contact.mobile]
                              .filter(Boolean)
                              .join(' · ')}
                          </div>
                        </div>
                      </div>
                      <IconButton icon={Trash2} label="Kontakt entfernen" variant="ghost" size="sm" onClick={() => removeContact(index)} />
                    </div>
                  ))}
                </div>
              )}

              {/* Add contact form */}
              {showContactForm && (
                <div className="rounded-lg border border-border p-4">
                  <p className="mb-4 t-small text-muted-foreground">Nur Vor- und Nachname sind Pflichtfelder.</p>
                  {contactErrors.general && (
                    <Callout tone="danger" icon={CircleAlert} title={contactErrors.general} boxed className="mb-4" />
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <TextField
                      label="Vorname"
                      required
                      value={newContact.first_name}
                      onChange={(e) => {
                        setNewContact({ ...newContact, first_name: e.target.value });
                        if (contactErrors.first_name) {
                          setContactErrors({ ...contactErrors, first_name: undefined });
                        }
                      }}
                      placeholder="Max"
                      error={contactErrors.first_name}
                    />
                    <TextField
                      label="Nachname"
                      required
                      value={newContact.last_name}
                      onChange={(e) => {
                        setNewContact({ ...newContact, last_name: e.target.value });
                        if (contactErrors.last_name) {
                          setContactErrors({ ...contactErrors, last_name: undefined });
                        }
                      }}
                      placeholder="Mustermann"
                      error={contactErrors.last_name}
                    />
                    <TextField
                      label="E-Mail"
                      type="email"
                      value={newContact.email}
                      onChange={(e) => {
                        setNewContact({ ...newContact, email: e.target.value });
                        if (contactErrors.email || contactErrors.general) {
                          setContactErrors({ ...contactErrors, email: undefined, general: undefined });
                        }
                      }}
                      placeholder="max@firma.de"
                      error={contactErrors.email}
                    />
                    <TextField
                      label="Position"
                      value={newContact.role}
                      onChange={(e) => setNewContact({ ...newContact, role: e.target.value })}
                      placeholder="Geschäftsführer"
                    />
                    <TextField
                      label="Telefon (Festnetz)"
                      value={newContact.phone}
                      onChange={(e) => {
                        setNewContact({ ...newContact, phone: filterPhoneInput(e.target.value) });
                        if (contactErrors.phone || contactErrors.general) {
                          setContactErrors({ ...contactErrors, phone: undefined, general: undefined });
                        }
                      }}
                      onBlur={() => {
                        // Beim Verlassen des Feldes automatisch formatieren
                        if (newContact.phone.trim()) {
                          const formatted = normalizePhoneNumber(newContact.phone);
                          setNewContact({ ...newContact, phone: formatted });
                        }
                      }}
                      placeholder="0351 1234567"
                      error={contactErrors.phone || getPhoneErrorMessage(newContact.phone)}
                      hint={!contactErrors.phone && !getPhoneErrorMessage(newContact.phone) ? "Wird beim Verlassen des Felds formatiert" : undefined}
                    />
                    <TextField
                      label="Mobil"
                      value={newContact.mobile}
                      onChange={(e) => {
                        setNewContact({ ...newContact, mobile: filterPhoneInput(e.target.value) });
                        if (contactErrors.mobile || contactErrors.general) {
                          setContactErrors({ ...contactErrors, mobile: undefined, general: undefined });
                        }
                      }}
                      onBlur={() => {
                        // Beim Verlassen des Feldes automatisch formatieren
                        if (newContact.mobile.trim()) {
                          const formatted = normalizePhoneNumber(newContact.mobile);
                          setNewContact({ ...newContact, mobile: formatted });
                        }
                      }}
                      placeholder="0170 1234567"
                      error={contactErrors.mobile || getPhoneErrorMessage(newContact.mobile)}
                      hint={!contactErrors.mobile && !getPhoneErrorMessage(newContact.mobile) ? "Wird beim Verlassen des Felds formatiert" : undefined}
                    />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => {
                        setShowContactForm(false);
                        setContactErrors({});
                        setNewContact({
                          first_name: '',
                          last_name: '',
                          email: '',
                          phone: '',
                          mobile: '',
                          role: '',
                          is_primary: false,
                        });
                      }}
                    >
                      Abbrechen
                    </Button>
                    <Button type="button" size="sm" variant="primary" onClick={addContact}>
                      Kontakt hinzufügen
                    </Button>
                  </div>
                </div>
              )}

              {/* Empty state */}
              {contacts.length === 0 && !showContactForm && (
                <EmptyState
                  icon={User}
                  title="Noch keine Ansprechpartner"
                  description="Ansprechpartner erscheinen hier, sobald einer hinzugefügt ist."
                  className="py-6"
                />
              )}
          </Card>
        )}

        {/* Notes */}
        <Card title="Notizen" description="Interne Anmerkungen zum Kunden">
            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <TextareaField
                  {...field}
                  rows={4}
                  placeholder="Interne Notizen zum Kunden"
                />
              )}
            />
        </Card>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => navigate(zurueckZiel)}
            disabled={isSubmitting}
          >
            Abbrechen
          </Button>
          <Button type="submit" variant="primary" loading={isSubmitting}>
            {isEdit ? 'Änderungen speichern' : 'Kunde anlegen'}
          </Button>
        </div>
      </form>

      <LostReasonModal
        open={showLost}
        onCancel={dialogeSchliessen}
        onSubmit={(reason) => {
          if (pendingStatus) {
            setValue('status', pendingStatus, { shouldDirty: true });
            setValue('lost_reason', reason, { shouldDirty: true });
          }
          dialogeSchliessen();
        }}
      />
      <FollowUpModal
        open={showFollowUp}
        initialValue={customer?.follow_up_at}
        onCancel={dialogeSchliessen}
        onSubmit={(iso) => {
          if (pendingStatus) {
            setValue('status', pendingStatus, { shouldDirty: true });
            setValue('follow_up_at', iso, { shouldDirty: true });
          }
          dialogeSchliessen();
        }}
      />
    </div>
  );
}
